import React, { useRef, useCallback, useEffect } from 'react';
import { cn } from '../lib/utils';

const ALLOWED_TAGS = ['B', 'STRONG', 'BR', 'P'];

function sanitizeHTML(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function walk(node) {
    let result = '';
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        // Escape any raw HTML characters in text
        result += child.textContent
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName;
        const inner = walk(child);
        if (ALLOWED_TAGS.includes(tag)) {
          const lower = tag.toLowerCase();
          if (lower === 'br') {
            result += '<br>';
          } else {
            result += `<${lower}>${inner}</${lower}>`;
          }
        } else {
          // Strip the tag but keep contents
          result += inner;
        }
      }
    }
    return result;
  }

  return walk(doc.body);
}

const RichPasteArea = ({ value, onChange, placeholder, className, ...props }) => {
  const editorRef = useRef(null);
  const isInternalUpdate = useRef(false);

  // Sync external value into the editor (only when value changes externally)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const el = editorRef.current;
    if (el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    isInternalUpdate.current = true;
    let html = el.innerHTML;
    // Normalize browser-generated bold spans to <b> tags
    html = html.replace(/<span[^>]*style="[^"]*font-weight:\s*(?:bold|700|800|900)[^"]*"[^>]*>(.*?)<\/span>/gi, '<b>$1</b>');
    // Treat empty editor states as empty string
    const cleaned = html === '<br>' || html === '<p><br></p>' ? '' : html;
    onChange(cleaned);
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    const html = clipboardData.getData('text/html');
    const plain = clipboardData.getData('text/plain');

    let sanitized;
    if (html) {
      sanitized = sanitizeHTML(html);
    } else {
      // Plain text: escape HTML and preserve line breaks
      sanitized = plain
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    }

    // Insert at cursor position
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const fragment = document.createRange().createContextualFragment(sanitized);
      range.insertNode(fragment);
      // Move cursor to end of inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    emitChange();
  }, [emitChange]);

  const handleKeyDown = useCallback((e) => {
    // Prevent formatting shortcuts except bold
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b' || e.key === 'B') {
        // Allow bold
        return;
      }
      if (e.key === 'i' || e.key === 'u') {
        e.preventDefault();
      }
    }
  }, []);

  const isEmpty = !value || value === '<br>' || value === '<p><br></p>';

  return (
    <div className="relative">
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'overflow-y-auto whitespace-pre-wrap break-words',
          className
        )}
        data-testid={props['data-testid']}
        role="textbox"
        aria-multiline="true"
        aria-placeholder={placeholder}
      />
      {isEmpty && (
        <div className="absolute top-2 left-3 text-sm text-muted-foreground pointer-events-none">
          {placeholder}
        </div>
      )}
    </div>
  );
};

export { RichPasteArea, sanitizeHTML };
